/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import type { EChartsOption } from "echarts";
import type { ECharts } from "echarts/core";
import { loadEcharts } from "./loadEcharts";
import { formatChartValue, formatChartValueCompact } from "./format";
import type { LemmonChartProps, LemmonChartSeries } from "./types";

export type { LemmonChartProps, LemmonChartSeries, LemmonChartValueFormat } from "./types";

const PALETTE = [
  "#2f7ded",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
];

/**
 * General-purpose echarts wrapper. Callers describe *what* to plot (rows,
 * a category key, a list of series) rather than an `EChartsOption` — echarts
 * owns axis layout (label rotation/thinning, tick density), which is the
 * actual fix for label collisions at large category counts: a manual
 * CSS-breakpoint-based thinning heuristic can't adapt to arbitrary data
 * length the way echarts' own `axisLabel.hideOverlap` can.
 */
export const LemmonChart = defineComponent<LemmonChartProps>({
  name: "LemmonChart",
  props: [
    "type",
    "data",
    "xKey",
    "series",
    "currency",
    "loading",
    "emptyMessage",
    "height",
    "valueAxis",
    "showLegend",
    "optionOverrides",
  ],
  setup(props) {
    const chartRef = ref<HTMLElement | null>(null);
    const chart = ref<ECharts | null>(null);
    const echartsReady = ref(false);

    const currency = computed(() => props.currency ?? "CHF");
    const xKey = computed(() => props.xKey ?? "x");
    const series = computed<LemmonChartSeries[]>(() => props.series ?? []);
    const data = computed<Array<Record<string, unknown>>>(() => props.data ?? []);
    const isEmpty = computed(() => !props.loading && data.value.length === 0);

    const categories = computed(() =>
      data.value.map((d) => String(d[xKey.value] ?? "")),
    );

    const buildPieOption = (): EChartsOption => {
      const s = series.value[0];
      const pieData = data.value.map((d, i) => ({
        name: String(d[xKey.value] ?? ""),
        value: Number(d[s?.key ?? ""]) || 0,
        itemStyle: { color: PALETTE[i % PALETTE.length] },
      }));
      return {
        tooltip: {
          trigger: "item",
          // Chart panels routinely sit inside `overflow-hidden`/`overflow-y-auto`
          // ancestors (rounded cards, scroll containers); appendToBody portals
          // the tooltip node to <body> so it's never clipped by them.
          appendToBody: true,
          confine: false,
          formatter: (params: any) =>
            `${params.name}: ${formatChartValue(params.value, s?.format, currency.value)} (${params.percent}%)`,
        },
        legend:
          props.showLegend === false
            ? undefined
            : { bottom: 0, type: "scroll" },
        series: [
          {
            type: "pie",
            radius: ["45%", "72%"],
            avoidLabelOverlap: true,
            itemStyle: { borderRadius: 4, borderColor: "#fff", borderWidth: 1 },
            label: { formatter: (p: any) => `${p.percent}%`, fontSize: 11 },
            data: pieData,
          },
        ],
      };
    };

    const buildAxisOption = (): EChartsOption => {
      const seriesOption = series.value.map((s, i) => ({
        name: s.name,
        type: props.type,
        stack: s.stack,
        // Straight segments, not a smoothed spline — a bezier curve through
        // only a handful of points routinely overshoots past the real
        // max/min (e.g. arcing above the highest data point), inventing a
        // value that was never in the data.
        symbolSize: props.type === "line" ? 6 : undefined,
        itemStyle: { color: s.color ?? PALETTE[i % PALETTE.length] },
        lineStyle: props.type === "line" ? { width: 2 } : undefined,
        data: data.value.map((d) => Number(d[s.key]) || 0),
      }));

      // Legend lives above the plot, not at the bottom — the bottom edge is
      // already claimed by x-axis labels, and a bottom-anchored legend sat
      // directly on top of them (see the "2026-08-04" collision).
      const showLegend = props.showLegend !== false && series.value.length > 1;

      return {
        grid: {
          top: showLegend ? 40 : 16,
          left: props.valueAxis === false ? 12 : 8,
          right: 16,
          bottom: 28,
          containLabel: true,
        },
        tooltip: {
          // `trigger: "axis"` never renders its content box here — it needs
          // per-series coordinate-system lookups across the whole axis, and
          // that resolution silently no-ops in this chart's mount sequence
          // (confirmed via a direct `dispatchAction({type:"showTip"})` call:
          // zero DOM nodes, zero errors). `trigger: "item"` — hit-testing the
          // single hovered graphic element — is what the hand-rolled tooltip
          // in RevenueAreaChart already uses successfully on this same
          // wrapper, so match that proven path instead of "axis".
          trigger: "item",
          triggerOn: "mousemove|click",
          // Chart panels routinely sit inside `overflow-hidden`/`overflow-y-auto`
          // ancestors (rounded cards, scroll containers); appendToBody portals
          // the tooltip node to <body> so it's never clipped by them.
          appendToBody: true,
          confine: false,
          formatter: (params: any) => {
            const list: any[] = Array.isArray(params) ? params : [params];
            const header = String(list[0]?.axisValueLabel ?? list[0]?.name ?? "");
            const rows = list
              .map((p) => {
                const s = series.value[p.seriesIndex];
                return `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:4px;">
                  <span>${p.marker}${p.seriesName}</span>
                  <strong style="white-space:nowrap;">${formatChartValue(p.value, s?.format, currency.value)}</strong>
                </div>`;
              })
              .join("");
            return `<div style="min-width:180px;"><div style="font-weight:600;margin-bottom:2px;">${header}</div>${rows}</div>`;
          },
        },
        legend: showLegend ? { top: 0, type: "scroll" } : undefined,
        xAxis: {
          type: "category",
          data: categories.value,
          boundaryGap: props.type === "bar",
          axisTick: { show: false },
          axisLine: { lineStyle: { color: "rgba(0,0,0,0.12)" } },
          axisLabel: {
            fontSize: 11,
            hideOverlap: true,
            // echarts computes the actual interval that fits — this just
            // caps how many labels it may show, so a 1-day range keeps every
            // label and a 1-year range doesn't jam 365 of them together.
            interval: categories.value.length > 40 ? "auto" : 0,
          },
          // Independent of tooltip.trigger — keeps the hover crosshair/shadow
          // guide across the whole axis even though the tooltip itself now
          // only fires per hovered graphic (see trigger: "item" above).
          axisPointer: {
            show: true,
            type: props.type === "bar" ? "shadow" : "line",
          },
        },
        yAxis: {
          type: "value",
          show: props.valueAxis !== false,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: "rgba(0,0,0,0.06)" } },
          axisLabel: {
            fontSize: 11,
            formatter: (v: number) =>
              formatChartValueCompact(v, series.value[0]?.format, currency.value),
          },
        },
        series: seriesOption as any,
        animationDurationUpdate: 300,
      };
    };

    const buildOption = (): EChartsOption => {
      const base = props.type === "pie" ? buildPieOption() : buildAxisOption();
      return props.optionOverrides ? props.optionOverrides(base) : base;
    };

    const updateChart = () => {
      if (!chart.value || props.loading || isEmpty.value) return;
      chart.value.setOption(buildOption(), { notMerge: true, lazyUpdate: false });
    };

    const resize = () => chart.value?.resize();

    const initChart = async () => {
      const core = await loadEcharts();
      echartsReady.value = true;
      await nextTick();
      if (!chartRef.value) return;
      if (chart.value) {
        chart.value.dispose();
        chart.value = null;
      }
      chart.value = core.init(chartRef.value, undefined, { renderer: "canvas" });
      (window as any).__lastChart = chart.value;
      updateChart();
      // The container's real size may not be committed yet on first paint
      // inside flex/absolute-positioned ancestors, which can leave echarts
      // unable to lay out a coordinate system for the option just set above —
      // resize + rebuild once layout has settled to recover from that.
      requestAnimationFrame(() => {
        chart.value?.resize();
        updateChart();
      });
    };

    onMounted(() => {
      void initChart();
      window.addEventListener("resize", resize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", resize);
      chart.value?.dispose();
      chart.value = null;
    });

    watch(
      () => [props.data, props.series, props.type, props.currency, props.loading, props.valueAxis, props.showLegend],
      () => nextTick(updateChart),
      { deep: true },
    );

    return () => {
      // No explicit height — fill whatever the parent constrains (e.g. an
      // `absolute inset-0` panel with its own min-height), matching plain
      // `<div class="h-full">` sizing. An explicit height sets a fixed px box.
      const sizeStyle =
        props.height != null ? { height: `${props.height}px` } : undefined;
      const sizeClass = props.height != null ? "" : "h-full";

      if (props.loading) {
        return (
          <div
            class={`w-full animate-pulse rounded-xl bg-base-200 ${sizeClass}`}
            style={sizeStyle}
          />
        );
      }

      if (isEmpty.value) {
        return (
          <div
            class={`grid place-items-center rounded-xl border border-dashed border-base-300 bg-base-100/40 text-center ${sizeClass}`}
            style={sizeStyle}
          >
            <div>
              <span class="material-symbols-outlined text-3xl text-base-content/35">
                bar_chart
              </span>
              <p class="mt-1 text-sm text-base-content/70">
                {props.emptyMessage ?? "No data"}
              </p>
            </div>
          </div>
        );
      }

      return (
        <div class={`relative w-full ${sizeClass}`} style={sizeStyle}>
          <div ref={chartRef} class="h-full w-full" />
        </div>
      );
    };
  },
});

export default LemmonChart;
